import { useCallback, useState } from "react";

import Tooltip from "components/ui/tooltip/Tooltip";
import { IconProps } from "../../../lib/icons/untitled_ui/UIcon";
import { EventData, EventNames, useTracking } from "../../../lib/tracking";
import { SharedSaveProps } from "../../entities/utils";
import Button, { ButtonType } from "../../ui/button/Button";
import { CreateLeadFormOutput } from "../create_lead/utils";
import SaveLeadDrawer from "./SaveLeadDrawer";

type Props = SharedSaveProps<CreateLeadFormOutput> & {
  trackingData: EventData;
  savedLeadIds: string[];
  buttonProps?: {
    buttonText?: string;
    buttonType?: ButtonType;
    icon?: IconProps | null;
  };
  // Has to be used when button is in a table row, in a table. Otherwise it won't open
  onShowDrawer?: () => void;
};

export function SaveLeadButton({
  entityId,
  entityType,
  buttonProps = {
    buttonType: "primary",
    icon: {
      icon: "bookmark",
      color: "sysTextWhite",
      fill: "sysTextWhite",
    },
  },
  trackingData,
  savedLeadIds,
  onShowDrawer,
  defaultValues,
}: Props) {
  const { logEvent } = useTracking();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const isSaved = savedLeadIds.length > 0;

  const handleSaveClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      logEvent(EventNames.saveDataClicked, {
        ...trackingData,
        "Data type": entityType.toUpperCase(),
      });

      if (onShowDrawer) {
        onShowDrawer();
      } else {
        setIsDrawerOpen(true);
      }
    },
    [entityType, logEvent, onShowDrawer, trackingData],
  );

  // Icon-only usage (empty buttonText) has no visible text; label the action clearly
  // rather than leaking the raw entity type (e.g. "contact_cognism").
  const iconOnly = buttonProps.buttonText === "";
  const saveLabel = iconOnly ? "Save to lead" : `Save ${entityType}`;

  return (
    <>
      <Tooltip content={saveLabel}>
        <Button
          buttonType={buttonProps.buttonType}
          onClick={handleSaveClick}
          icon={buttonProps.icon?.icon}
          aria-label={iconOnly ? saveLabel : undefined}
          iconProps={{
            ...buttonProps.icon,
            fill: isSaved ? buttonProps.icon?.fill : "transparent",
          }}
        >
          {buttonProps.buttonText ?? (isSaved ? `Saved (${savedLeadIds.length})` : "Save")}
        </Button>
      </Tooltip>
      {!onShowDrawer && (
        <SaveLeadDrawer
          isOpen={isDrawerOpen}
          onOpenChange={setIsDrawerOpen}
          savedLeadIds={savedLeadIds}
          entityType={entityType}
          entityId={entityId}
          defaultValues={defaultValues}
          trackingData={trackingData}
        />
      )}
    </>
  );
}
